import { attachInstallIdToUrl, Completion, Interaction } from '@youversion/analytics-youversion-nextjs';
import { Button, Heading, Input, LinkButton, Text } from '@youversion/design-system-components';
import { LoginEnabledLocale } from '@youversion/types';
import { Form, Formik } from 'formik';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { setCookie } from 'nookies';
import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { FullPageLoader } from '@/components/Loader';
import { Sidebar } from '@/components/Sidebar';
import ThirdPartyOptions from '@/components/ThirdPartyOptions/ThirdPartyOptions';
import { IS_STAGING, LOGIN_CLIENT_ID, LOGIN_CLIENT_SECRET } from '@/env';
import { useQueryParams, useUXHooks } from '@/hooks';
import { authentication, initialize, redirectWithToken } from '@/lib/web-auth-browser';
import { analyticsElementIds } from '@/utils/analytics/elements';
import { DEFAULT_TARGET_APPLICATION, getClientSecret, isValidRedirectUri } from '@/utils/supported-redirects';
import { YVTempAccount } from '@/utils/types';

interface Values {
  password: string;
  username: string;
}

const schema = Yup.object().shape({
  password: Yup.string().required(),
  username: Yup.string().required(),
});

function isSubmittable(values: Values) {
  return values.username && values.password;
}

export default function HomePage() {
  const { t } = useTranslation(['common', 'createAccount']);
  const router = useRouter();
  const { main } = useUXHooks();

  const [error, setError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState<'pending' | 'resolved' | 'rejected' | 'idle'>('pending');
  const accountCreationError = router.query.accountCreationError;
  const [showConnectHelpForm, setShowConnectHelpForm] = useState(false);

  useEffect(() => {
    const isFromYVConnect = document.referrer.includes('connect.youversion.com');
    setShowConnectHelpForm(isFromYVConnect);
  }, []);

  const {
    app_ref,
    client_id: clientIdParam,
    confirm_apple_account: confirmAppleAccount,
    intent,
    redirect_uri: redirectUriParam,
    is_account_invite: isAccountInvite,
    scopes,
  } = useQueryParams();

  const [isConfirmingApple, setIsConfirmingApple] = useState(() => confirmAppleAccount === 'true');

  const checkForMultipleAccounts = useCallback(async () => {
    const availableTokens = await authentication.getAvailableTokens();
    if (availableTokens.tokens.length === 0) {
      setLoadingStatus('resolved');
      return;
    } else if (availableTokens.tokens.length > 0) {
      if (router.isReady) {
        return await router.push({
          pathname: '/choose-account',
          query: router.query,
        });
      }
      return;
    }
  }, [router]);

  const loginForRedirect = useCallback(async () => {
    // If no params are provided, we want to redirect to the user to bible.com, the default target of login.youversion.
    const clientId = clientIdParam ?? DEFAULT_TARGET_APPLICATION?.id;
    const clientSecret = getClientSecret(clientId) ?? DEFAULT_TARGET_APPLICATION?.secret;
    const redirectUri = redirectUriParam ?? DEFAULT_TARGET_APPLICATION?.redirect;

    try {
      if (clientId && clientSecret && redirectUri && isValidRedirectUri({ clientId, redirectUri })) {
        const tokenForRedirect = await authentication.loginWithTokenForRedirect({
          clientId,
          clientSecret,
          scopes: scopes ? scopes.split(' ') : [],
        });

        Completion.Auth.fire({
          authType: 'email',
          completionContext: 'login',
          success: true,
          userId: tokenForRedirect.user.id,
        });

        redirectWithToken(tokenForRedirect, attachInstallIdToUrl(redirectUri));

        return;
      }
      await router.push('/profile');
    } catch {
      await router.push('/profile');
    }
  }, [clientIdParam, redirectUriParam, router, scopes]);

  /**
   * This Function checks if authentication is available. If not, initialize the app for authentication checks.
   */
  const ensureInitialized = useCallback(async () => {
    if (authentication) {
      return;
    }
    let token = await initialize({ clientId: LOGIN_CLIENT_ID, clientSecret: LOGIN_CLIENT_SECRET, staging: IS_STAGING });
    if (token) {
      return;
    }
  }, []);

  const handleRedirects = useCallback(async () => {
    try {
      if (router.query.type === 'add_account') {
        setLoadingStatus('resolved');
      } else {
        await checkForMultipleAccounts();
      }
    } catch {
      setLoadingStatus('rejected');
    }
  }, [checkForMultipleAccounts, router.query.type]);

  const isRedirectUrlAnInviteUrl = isAccountInvite === 'true';

  useEffect(() => {
    (async () => {
      await ensureInitialized();

      if (isRedirectUrlAnInviteUrl) {
        await authentication.logout();
      }

      await handleRedirects();
    })();
  }, [ensureInitialized, handleRedirects, isRedirectUrlAnInviteUrl]);

  async function handleSubmit(values: Values) {
    Interaction.None.fire({ elementId: analyticsElementIds.signIn, elementState: 'selecting', success: true });

    try {
      await authentication.loginWithEmail(values.username, values.password);
      await loginForRedirect();
    } catch (err) {
      onError(err);

      Completion.Auth.fire({
        authType: 'email',
        completionContext: 'login',
        error: getErrorMessage(err),
        percentComplete: 0,
        success: false,
        userId: null,
      });
    }
  }

  function storeQueries() {
    const yvTempAccount: YVTempAccount = {
      app_ref: app_ref ?? '',
      client_id: clientIdParam ?? '',
      i18n_locale_tag: (router.locale as LoginEnabledLocale) ?? '',
      intent: intent ?? '',
      redirect_uri: redirectUriParam ?? '',
    };
    localStorage.setItem('yv_temp_account', JSON.stringify(yvTempAccount));
    setCookie(null, 'yv_temp_account', JSON.stringify(yvTempAccount), { path: '/' });
  }

  const getErrorMessage = useCallback(
    (error: unknown) => {
      if (error instanceof Error) {
        if (error.message.startsWith('Invalid request: 403')) {
          return t('common:errors.generic');
        } else if (
          error.message.startsWith('Invalid request: 401') ||
          error.message.startsWith('Failed to refresh token')
        ) {
          return t('common:errors.invalid_credentials');
        } else {
          return error.message;
        }
      } else {
        return t('common:errors.generic');
      }
    },
    [t]
  );

  const onError = useCallback(
    (error: unknown) => {
      console.error(error);
      setError(getErrorMessage(error));
    },
    [getErrorMessage]
  );

  // Full-page loading spinner to match other apps, which creates a seamless experience for signed-in users.
  if (loadingStatus === 'pending') {
    return <FullPageLoader />;
  }

  return (
    <>
      <Head>
        <title>{t('common:sign_in_to_youversion')}</title>
      </Head>

      <div className='h-screen flex'>
        <Sidebar />
        <div className='big-container py-6 lg:py-6 w-full overflow-y-scroll '>
          <main className='w-full max-w-[480px] px-3 md:m-auto lg:m-auto lg:pbs-12'>
            {isConfirmingApple ? null : (
              <>
                <Heading
                  className='whitespace-nowrap pbe-2 text-text-light dark:text-text-dark'
                  level={1}
                  size='xl'
                  variant='headline'
                >
                  {t('common:sign_in')}
                </Heading>
                {isRedirectUrlAnInviteUrl ? null : (
                  <Text
                    className='lg:flex hidden text-[14px] items-center gap-1 text-text-light dark:text-text-dark'
                    size='sm'
                    variant='paragraph'
                  >
                    {main.noAccount.description}
                    <LinkButton
                      component={Link}
                      // @ts-expect-error -- url is of type UrlObject, which is accepted by the Next.js Link component, but href is of type 'string'
                      href={main.noAccount.link.url}
                      onClick={() =>
                        Interaction.None.fire(
                          {
                            elementId: analyticsElementIds.createAccountBtn,
                            elementState: 'selecting',
                            success: true,
                          },
                          { fireImmediately: true }
                        )
                      }
                      size='xs'
                      variant='strokedSecondary'
                    >
                      {main.noAccount.link.text}
                    </LinkButton>
                  </Text>
                )}
              </>
            )}

            {accountCreationError ? (
              <div className='bg-yellow-10 text-yellow-40 rounded p-2 mbs-4'>
                <Trans
                  components={{
                    new_account_link: (
                      <a
                        className='text-yellow-40'
                        href={`https://login${IS_STAGING ? '-staging' : ''}.youversion.com/create-account`}
                      />
                    ),
                  }}
                  i18nKey={t('createAccount:errors.account_creation')}
                />
              </div>
            ) : null}
            {error ? <div className='bg-orange-10 text-orange-40 rounded p-2 mbs-4'>{error}</div> : null}
            {main.secondaryText ? (
              <div className='mbs-2 text-text-light dark:text-text-dark'>{main.secondaryText}</div>
            ) : null}

            {isRedirectUrlAnInviteUrl ? null : (
              <ThirdPartyOptions isAppleQuery={setIsConfirmingApple} isConfirmingApple={isConfirmingApple} />
            )}
            {isConfirmingApple ? null : (
              <>
                {isRedirectUrlAnInviteUrl ? null : (
                  <div className='flex items-center mbs-3'>
                    <Text
                      className='flex-grow-0 mli-1.5 text-text-light dark:text-text-dark'
                      size='md'
                      variant='caption'
                    >
                      {t('common:or')}
                    </Text>
                  </div>
                )}
                <Formik
                  initialValues={{ password: '', username: '' }}
                  onSubmit={handleSubmit}
                  validationSchema={schema}
                >
                  {({ handleChange, handleSubmit, values }) => (
                    <Form className='flex flex-col gap-3 w-full mbs-3' onSubmit={handleSubmit}>
                      <Input
                        className='text-text-light dark:text-text-dark'
                        hint={isRedirectUrlAnInviteUrl ? t('common:use_email_from_invite') : undefined}
                        label={t('common:username')}
                        name='username'
                        onChange={handleChange}
                        value={values.username}
                      />
                      <Input
                        className='text-text-light dark:text-text-dark'
                        label={t('common:password')}
                        name='password'
                        onChange={handleChange}
                        type='password'
                        value={values.password}
                      />
                      <div className='flex w-full items-center justify-between'>
                        <Button
                          className='dark:disabled:bg-gray-30 dark:disabled:text-gray-10'
                          disabled={!isSubmittable(values)}
                          size='md'
                          type='submit'
                          variant='brand'
                        >
                          {t('common:sign_in')}
                        </Button>
                        <Link
                          className='no-underline text-text-light dark:text-text-dark text-13'
                          href='/forgot-password'
                          onClick={() => {
                            Interaction.None.fire(
                              {
                                elementId: analyticsElementIds.forgotPasswordBtn,
                                elementState: 'selecting',
                                success: true,
                              },
                              { fireImmediately: true }
                            );
                          }}
                        >
                          {t('common:forgot_password')}
                        </Link>
                      </div>
                      <footer>
                        {main.footer ? (
                          <div className='text-13 text-text-light dark:text-text-dark'>{main.footer}</div>
                        ) : (
                          <Text
                            className='text-text-light dark:text-text-dark'
                            component='a'
                            href={
                              showConnectHelpForm
                                ? 'https://lifechurch.formstack.com/forms/yvconnect_support'
                                : 'https://lifechurch.formstack.com/forms/supportform'
                            }
                            size='md'
                            variant='caption'
                          >
                            {t('common:intent.content_partners.footer')}
                          </Text>
                        )}
                      </footer>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </main>
          {isConfirmingApple || isRedirectUrlAnInviteUrl ? null : (
            <div className='bottom-link flex justify-center items-center w-full mbs-8 lg:hidden'>
              <Text className='pis-3 text-text-light dark:text-text-dark' size='sm' variant='paragraph'>
                {main.noAccount.description}
                <Link
                  className="no-underline text-text-light dark:text-text-dark font-bold text-sm before:content-['\00a0'] overflow-hidden"
                  href={main.noAccount.link.url}
                  onClick={() => {
                    storeQueries();
                    Interaction.None.fire({
                      elementId: analyticsElementIds.createAccountBtn,
                      elementState: 'selecting',
                      success: true,
                    });
                  }}
                >
                  {main.noAccount.link.text}
                </Link>
              </Text>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['common', 'createAccount'])),
    },
  };
};
